import { FC } from 'react';
import type { CancellationStatus } from 'state/secondaryMarket/shared/cancellation/cancellation.model';

import { AlertBanner } from '../../../components/shared/AlertBanner/AlertBanner';
import { ConfirmationBanner } from '../../../components/shared/ConfirmationBanner/ConfirmationBanner';

interface SecondaryOrderInstantBookWorkflowProps {
  orderId: string;
  orderInstantBookStatus: CancellationStatus | undefined;
  orderInstantBookError: string | undefined;
  onInstantBookWorkflowConfirmed: (orderId: string) => void;
  onInstantBookWorkflowAborted: (orderId: string) => void;
  onInstantBookWorkflowCompleted: (orderId: string) => void;
}

export const SecondaryOrderInstantBookWorkflow: FC<SecondaryOrderInstantBookWorkflowProps> = ({
  orderId,
  orderInstantBookStatus,
  orderInstantBookError,
  onInstantBookWorkflowConfirmed,
  onInstantBookWorkflowAborted,
  onInstantBookWorkflowCompleted,
}) => {
  switch (orderInstantBookStatus) {
    case 'INIT': {
      return (
        <ConfirmationBanner
          icon="report_problem"
          e2e="order-instant-book-confirmation-banner"
          color="info"
          className="position-absolute bottom-0 start-0 end-0"
          message="secondaryMarketOrderBlotter.instantBook.confirm"
          confirmButtonText="form.Yes"
          cancelButtonText="form.No"
          onCancel={() => onInstantBookWorkflowAborted(orderId)}
          onConfirm={() => onInstantBookWorkflowConfirmed(orderId)}
        />
      );
    }
    case 'IN_PROGRESS': {
      return (
        <AlertBanner
          spin
          e2e="order-instant-book-alert-banner-in-progress"
          color="filling-info"
          className="position-absolute bottom-0 start-0 end-0"
          messages={['secondaryMarketOrderBlotter.instantBook.inProgress']}
          icon="autorenew"
        />
      );
    }
    case 'SUCCESS': {
      return (
        <AlertBanner
          color="primary"
          e2e="order-instant-book-alert-banner-success"
          className="position-absolute bottom-0 start-0 end-0"
          messages={['secondaryMarketOrderBlotter.instantBook.success']}
          icon="check_circle"
          dismissible
          onDismissed={() => onInstantBookWorkflowCompleted(orderId)}
        />
      );
    }
    case 'REJECTED':
    case 'FAILED': {
      const message =
        orderInstantBookError !== undefined
          ? [orderInstantBookError]
          : orderInstantBookStatus === 'REJECTED'
            ? ['secondaryMarketOrderBlotter.instantBook.rejected']
            : ['secondaryMarketOrderBlotter.instantBook.error'];
      return (
        <AlertBanner
          color="warning"
          e2e="order-instant-book-alert-banner-error"
          className="position-absolute bottom-0 start-0 end-0"
          messages={message}
          icon="error_outline"
          dismissible
          onDismissed={() => onInstantBookWorkflowCompleted(orderId)}
        />
      );
    }
    default:
      return null;
  }
};

SecondaryOrderInstantBookWorkflow.displayName = 'SecondaryOrderInstantBookWorkflow';
